import { describeError, useApi } from '@classgo/app-kit';
import { BrandLockup, Button, Card, EmptyState, Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';

const score = (value: number | null) =>
  value === null
    ? '—'
    : value.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * A student's report card for one period, laid out to print: school and student header,
 * subjects with grade, national label, teacher and observation, and the attendance summary.
 */
export const ReportCardScreen = () => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const { studentId = '' } = useParams({ strict: false });
  const years = useQuery(listQuery(api, 'years'));
  const activeYear =
    years.data?.items.find((year) => year.status === 'active') ?? years.data?.items[0];
  const periods = useQuery({
    ...listQuery(api, 'periods', { schoolYearId: activeYear?.id ?? '' }),
    enabled: !!activeYear,
  });
  const [periodId, setPeriodId] = useState('');
  useEffect(() => {
    if (!periodId && periods.data?.items[0]) setPeriodId(periods.data.items[0].id);
  }, [periodId, periods.data]);
  const card = useQuery(recordQueries.reportCard(api, studentId, periodId));
  const data = card.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div className="flex items-end gap-3">
          <Link
            to="/estudiantes/$studentId"
            params={{ studentId }}
            className="pb-2 text-sm text-brand-600 underline underline-offset-2 hover:decoration-2"
          >
            ← {t('student.title')}
          </Link>
          <Select
            label={t('reportCard.filter.period')}
            value={periodId}
            onChange={(event) => setPeriodId(event.target.value)}
            options={(periods.data?.items ?? []).map((period) => ({
              value: period.id,
              label: period.name,
            }))}
          />
        </div>
        <Button variant="secondary" onClick={() => window.print()}>
          {t('reportCard.print')}
        </Button>
      </div>
      {card.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, card.error)} />
      ) : !data ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <Card className="print:shadow-none">
          <article aria-label={t('reportCard.title')} className="space-y-6">
            <header className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 pb-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">{session.tenant.name}</p>
                <h2 className="text-2xl font-semibold text-gray-900">{t('reportCard.title')}</h2>
                <p className="text-sm text-gray-500">
                  {t('reportCard.subtitle', {
                    year: data.schoolYearName,
                    period: data.period.name,
                  })}{' '}
                  · {formatDate(data.period.startsOn)} → {formatDate(data.period.endsOn)}
                </p>
              </div>
              <BrandLockup height={28} />
            </header>
            <dl className="grid gap-4 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500">{t('reportCard.field.student')}</dt>
                <dd className="font-medium text-gray-900">
                  {data.student.givenName} {data.student.familyName}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('reportCard.field.document')}</dt>
                <dd className="font-medium text-gray-900">
                  {data.student.documentNumber
                    ? `${data.student.documentType} ${data.student.documentNumber}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('reportCard.field.group')}</dt>
                <dd className="font-medium text-gray-900">
                  {data.group.gradeName ? `${data.group.gradeName} · ` : ''}
                  {data.group.name}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('reportCard.field.homeroom')}</dt>
                <dd className="font-medium text-gray-900">{data.group.homeroomTeacher ?? '—'}</dd>
              </div>
            </dl>
            <table className="w-full text-sm" aria-label={t('reportCard.title')}>
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-3 font-semibold">{t('reportCard.column.subject')}</th>
                  <th className="pb-2 pr-3 font-semibold">{t('reportCard.column.teacher')}</th>
                  <th className="pb-2 pr-3 text-right font-semibold">
                    {t('reportCard.column.score')}
                  </th>
                  <th className="pb-2 pr-3 font-semibold">{t('reportCard.column.national')}</th>
                  <th className="pb-2 font-semibold">{t('reportCard.column.observation')}</th>
                </tr>
              </thead>
              <tbody>
                {data.subjects.map((subject) => (
                  <tr key={subject.subjectId} className="border-t border-gray-100 align-top">
                    <td className="py-2 pr-3 font-medium text-gray-900">{subject.subjectName}</td>
                    <td className="py-2 pr-3 text-gray-600">{subject.teacherName ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                      {score(subject.score)}
                      {subject.published ? (
                        ''
                      ) : (
                        <span
                          className="ml-1 text-xs font-normal text-warning-700"
                          title={t('reportCard.draft')}
                        >
                          *
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">{subject.nationalLabel ?? '—'}</td>
                    <td className="py-2 text-gray-600">{subject.observation ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <section>
              <h3 className="text-sm font-semibold text-gray-900">{t('reportCard.attendance')}</h3>
              <p className="mt-1 text-sm text-gray-600">
                {t('reportCard.attendance.summary', data.attendance)}
              </p>
            </section>
            <footer className="border-t border-gray-200 pt-3 text-xs text-gray-500">
              {t('reportCard.generated', {
                date: new Date(data.generatedAt).toLocaleString('es-CO'),
              })}{' '}
              · {t('reportCard.preview')}
            </footer>
          </article>
        </Card>
      )}
    </div>
  );
};
